'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  createSpaceSettingsService,
  deleteSpaceSettingsService,
  updateSpaceSettingsService,
} from '@/services/httpServices/SpacesServices';
import type {
  CreateSpaceSettingsServiceConfig,
  DeleteSpaceSettingsServiceConfig,
  UpdateSpaceSettingsServiceConfig,
} from '@/services/httpServices/SpacesServices/types';

import { spaceSettingsKeys, spacesListKeys } from '../queries/spaceQueryKeys';
import { spaceLanguagesKeys } from '../queries/userAddonsQueryKeys';

/**
 * Mutation hook for creating a new space with settings
 */
export function useCreateSpaceSettingsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: CreateSpaceSettingsServiceConfig) => {
      try {
        return await createSpaceSettingsService(config);
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Failed to create space settings',
        );
      }
    },
    onSuccess: async () => {
      // Invalidate spaces list queries to show the new space
      await queryClient.invalidateQueries({
        queryKey: spacesListKeys(),
      });

      // Invalidate user role queries to refresh permissions for new space
      // Creator gets permissions for the new space
      await queryClient.invalidateQueries({
        queryKey: ['userRole'],
      });
    },
  });
}

/**
 * Mutation hook for updating space settings
 */
export function useUpdateSpaceSettingsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: UpdateSpaceSettingsServiceConfig) => {
      try {
        const response = await updateSpaceSettingsService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Failed to update space settings',
        );
      }
    },
    onSuccess: async (_, variables) => {
      // Invalidate spaces list queries
      await queryClient.invalidateQueries({
        queryKey: spacesListKeys(),
      });

      // Invalidate the specific space settings query
      await queryClient.invalidateQueries({
        queryKey: spaceSettingsKeys({
          httpRequestConfig: variables.httpRequestConfig,
          params: {
            path: {
              spaceId: variables.params?.path.spaceId ?? '',
            },
          },
        }),
      });

      // Invalidate spaceLanguages so the editing language picker updates immediately
      await queryClient.invalidateQueries({
        queryKey: spaceLanguagesKeys({ httpRequestConfig: variables.httpRequestConfig }),
      });

      // Invalidate user role queries to refresh permissions after user changes
      // This ensures UI reflects updated user roles/permissions immediately
      await queryClient.invalidateQueries({
        queryKey: ['userRole'],
      });
    },
  });
}

/**
 * Mutation hook for deleting a space
 */
export function useDeleteSpaceSettingsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: DeleteSpaceSettingsServiceConfig) => {
      try {
        await deleteSpaceSettingsService(config);
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Failed to delete space',
        );
      }
    },
    onSuccess: async () => {
      // Invalidate spaces list queries
      await queryClient.invalidateQueries({
        queryKey: spacesListKeys(),
      });

      // Invalidate user role queries to refresh permissions after space deletion
      // Users who had access to deleted space need updated permissions
      await queryClient.invalidateQueries({
        queryKey: ['userRole'],
      });
    },
  });
}
